import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { useAccounting } from "@/contexts/AccountingContext";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import {
  Building2, Clock, CheckCircle2, IndianRupee, ShieldCheck,
  FileText, BarChart3, Calendar, Users, MessageSquare,
  TrendingUp, Briefcase, Star, AlertTriangle, ArrowRight, Loader2
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend, AreaChart, Area
} from "recharts";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

const StatCard = ({ label, value, sub, icon: Icon, color }: { label: string; value: string; sub?: string; icon: any; color: string }) => (
  <Card className="bg-card border-border">
    <CardContent className="p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-muted-foreground">{label}</span>
        <div className={`w-8 h-8 rounded-lg ${color} flex items-center justify-center`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="text-xl font-bold font-display text-foreground">{value}</div>
      {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
    </CardContent>
  </Card>
);

const CADashboard = () => {
  const { vouchers, ledgers } = useAccounting();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [caProfile, setCaProfile] = useState<any>(null);
  const [clients, setClients] = useState<any[]>([]);
  const [serviceRequests, setServiceRequests] = useState<any[]>([]);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [unreadMessages, setUnreadMessages] = useState(0);

  useEffect(() => {
    if (user) fetchAllData();
  }, [user]);

  const fetchAllData = async () => {
    setLoading(true);
    const [profileRes, clientsRes, requestsRes, txRes, msgRes] = await Promise.all([
      supabase.from("ca_profiles").select("*").eq("user_id", user!.id).maybeSingle(),
      supabase.from("ca_company_access").select("*").eq("ca_user_id", user!.id),
      supabase.from("service_requests").select("*").order("created_at", { ascending: false }),
      supabase.from("marketplace_transactions").select("*, service_requests(service_type, company_name)").eq("ca_user_id", user!.id).order("created_at", { ascending: false }),
      supabase.from("marketplace_messages").select("id, is_read").eq("is_read", false),
    ]);

    if (profileRes.data) setCaProfile(profileRes.data);
    if (clientsRes.data) setClients(clientsRes.data as any[]);
    if (requestsRes.data) setServiceRequests(requestsRes.data as any[]);
    if (txRes.data) setTransactions(txRes.data as any[]);
    if (msgRes.data) setUnreadMessages(msgRes.data.length);
    setLoading(false);
  };

  // Accounting data
  const accountingData = useMemo(() => {
    const pending = vouchers.filter((v) => v.status === "Pending");
    const approved = vouchers.filter((v) => v.status === "Approved");
    const rejected = vouchers.filter((v) => v.status === "Rejected");
    const sales = approved.filter((v) => v.type === "Sales");
    const purchases = approved.filter((v) => v.type === "Purchase");
    const gstOutput = sales.reduce((s, v) => s + (v.cgst || 0) + (v.sgst || 0) + (v.igst || 0), 0);
    const gstInput = purchases.reduce((s, v) => s + (v.cgst || 0) + (v.sgst || 0) + (v.igst || 0), 0);
    return { pending, approved, rejected, gstOutput, gstInput, netGst: gstOutput - gstInput };
  }, [vouchers]);

  // Revenue stats
  const revenueStats = useMemo(() => {
    const totalEarned = transactions.reduce((s, t) => s + Number(t.net_amount || 0), 0);
    const totalService = transactions.reduce((s, t) => s + Number(t.service_amount || 0), 0);
    const paidTx = transactions.filter((t) => t.payment_status === "paid");
    const pendingTx = transactions.filter((t) => t.payment_status === "unpaid");
    return { totalEarned, totalService, paid: paidTx.length, pending: pendingTx.length, total: transactions.length };
  }, [transactions]);

  // Request stats
  const requestStats = useMemo(() => {
    const open = serviceRequests.filter((r) => r.status === "open").length;
    const inProgress = serviceRequests.filter((r) => r.status === "in_progress").length;
    const completed = serviceRequests.filter((r) => r.status === "completed").length;
    return { open, inProgress, completed, total: serviceRequests.length };
  }, [serviceRequests]);

  // Client stats
  const clientStats = useMemo(() => {
    const approved = clients.filter((c) => c.status === "approved").length;
    const pending = clients.filter((c) => c.status === "pending").length;
    return { approved, pending, total: clients.length };
  }, [clients]);

  // Chart data - Revenue by month
  const revenueByMonth = useMemo(() => {
    const months: Record<string, number> = {};
    transactions.forEach((t) => {
      const d = new Date(t.created_at);
      const key = d.toLocaleDateString("en-IN", { month: "short" });
      months[key] = (months[key] || 0) + Number(t.net_amount || 0);
    });
    return Object.entries(months).map(([month, amount]) => ({ month, amount }));
  }, [transactions]);

  // Chart data - Request status distribution
  const requestPieData = [
    { name: "Open", value: requestStats.open, color: "hsl(var(--primary))" },
    { name: "In Progress", value: requestStats.inProgress, color: "hsl(var(--accent))" },
    { name: "Completed", value: requestStats.completed, color: "hsl(var(--success, 142 76% 36%))" },
  ].filter((d) => d.value > 0);

  // GST trend
  const gstTrend = [
    { month: "Oct", output: 38000, input: 22000 },
    { month: "Nov", output: 42000, input: 28000 },
    { month: "Dec", output: 36000, input: 20000 },
    { month: "Jan", output: 45000, input: 30000 },
    { month: "Feb", output: 40000, input: 25000 },
    { month: "Mar", output: accountingData.gstOutput, input: accountingData.gstInput },
  ];

  // Compliance calendar
  const complianceItems = [
    { item: "GSTR-1 (March 2026)", due: "11 Apr 2026", status: "upcoming" },
    { item: "GSTR-3B (March 2026)", due: "20 Apr 2026", status: "upcoming" },
    { item: "TDS Return Q4", due: "15 Apr 2026", status: "upcoming" },
    { item: "GSTR-1 (February 2026)", due: "11 Mar 2026", status: "filed" },
    { item: "GSTR-3B (February 2026)", due: "20 Mar 2026", status: "filed" },
  ];

  const formatINR = (n: number) => `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-display text-foreground flex items-center gap-2">
            CA Dashboard
            {caProfile?.is_verified && (
              <Badge className="bg-green-500/10 text-green-600 border-green-500/20 text-[10px] gap-1">
                <ShieldCheck className="h-3 w-3" /> Verified
              </Badge>
            )}
          </h1>
          <p className="text-sm text-muted-foreground">
            Welcome back{caProfile?.name ? `, ${caProfile.name}` : ""}. Here's your practice overview.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="gap-1.5 text-xs" onClick={() => navigate("/dashboard/service-requests")}>
            <Briefcase className="h-3.5 w-3.5" /> Service Requests
          </Button>
          <Button size="sm" variant="outline" className="gap-1.5 text-xs" onClick={() => navigate("/dashboard/gst")}>
            <BarChart3 className="h-3.5 w-3.5" /> GST Reports
          </Button>
        </div>
      </div>

      {/* Top Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard label="Client Companies" value={String(clientStats.approved)} sub={`${clientStats.pending} pending`} icon={Building2} color="bg-primary/10 text-primary" />
        <StatCard label="Open Requests" value={String(requestStats.open)} sub={`${requestStats.inProgress} in progress`} icon={FileText} color="bg-accent/10 text-accent" />
        <StatCard label="Total Revenue" value={formatINR(revenueStats.totalEarned)} sub={`${revenueStats.total} transactions`} icon={IndianRupee} color="bg-green-500/10 text-green-600" />
        <StatCard label="Unread Messages" value={String(unreadMessages)} icon={MessageSquare} color="bg-blue-500/10 text-blue-600" />
        <StatCard label="Pending Reviews" value={String(accountingData.pending.length)} sub="Entries to approve" icon={Clock} color="bg-yellow-500/10 text-yellow-600" />
        <StatCard label={accountingData.netGst > 0 ? "GST Net Payable" : "Excess ITC"} value={formatINR(Math.abs(accountingData.netGst))} sub={accountingData.netGst > 0 ? "Payable" : "Carried Forward"} icon={ShieldCheck} color={accountingData.netGst > 0 ? "bg-destructive/10 text-destructive" : "bg-green-500/10 text-green-600"} />
      </div>

      {/* Revenue Chart + Request Status */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="bg-card border-border lg:col-span-2">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                <TrendingUp className="h-4 w-4 text-accent" /> Revenue from Services
              </h3>
              <Button size="sm" variant="ghost" className="text-xs gap-1" onClick={() => navigate("/dashboard/marketplace-transactions")}>
                View All <ArrowRight className="h-3 w-3" />
              </Button>
            </div>
            {revenueByMonth.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={revenueByMonth}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                  <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}K`} />
                  <Tooltip formatter={(v: number) => formatINR(v)} />
                  <Area type="monotone" dataKey="amount" name="Net Revenue" fill="hsl(var(--accent) / 0.15)" stroke="hsl(var(--accent))" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[240px] text-xs text-muted-foreground">
                No revenue data yet. Accept proposals to start earning.
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-foreground mb-4">Service Request Status</h3>
            {requestPieData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie data={requestPieData} cx="50%" cy="50%" innerRadius={40} outerRadius={65} paddingAngle={4} dataKey="value">
                      {requestPieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-1.5 mt-2">
                  {requestPieData.map((e, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: e.color }} />
                        <span className="text-muted-foreground">{e.name}</span>
                      </div>
                      <span className="font-medium text-foreground">{e.value}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-[200px] text-xs text-muted-foreground">
                No service requests yet.
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Client Companies + GST Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Client Companies */}
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                <Building2 className="h-4 w-4 text-primary" /> Client Companies
              </h3>
              <Button size="sm" variant="ghost" className="text-xs gap-1" onClick={() => navigate("/dashboard/ca-clients")}>
                Manage <ArrowRight className="h-3 w-3" />
              </Button>
            </div>
            {clients.length === 0 ? (
              <div className="text-center py-8">
                <Building2 className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
                <p className="text-xs text-muted-foreground">No client companies yet. Request access to start.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {clients.slice(0, 5).map((c) => (
                  <div key={c.id} className="flex items-center justify-between p-2.5 rounded-lg bg-muted/30 border border-border/50">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Building2 className="h-3.5 w-3.5 text-primary" />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-foreground">{c.company_name}</p>
                        {c.company_gstin && <p className="text-[10px] text-muted-foreground">{c.company_gstin}</p>}
                      </div>
                    </div>
                    <Badge variant="outline" className={`text-[10px] ${
                      c.status === "approved" ? "bg-green-500/10 text-green-600 border-green-500/20" :
                      c.status === "pending" ? "bg-yellow-500/10 text-yellow-600 border-yellow-500/20" :
                      "bg-destructive/10 text-destructive border-destructive/20"
                    }`}>
                      {c.status}
                    </Badge>
                  </div>
                ))}
                {clients.length > 5 && (
                  <p className="text-[10px] text-muted-foreground text-center mt-1">+{clients.length - 5} more</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* GST Tax Trend */}
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-1.5">
              <ShieldCheck className="h-4 w-4 text-accent" /> GST Tax Trend
            </h3>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={gstTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}K`} />
                <Tooltip formatter={(v: number) => formatINR(v)} />
                <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="output" name="Output Tax" stroke="hsl(var(--destructive))" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="input" name="Input Credit" stroke="hsl(var(--accent))" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* GST Filing Reminders + Recent Service Requests */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* GST Filing Reminders */}
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="h-4 w-4 text-accent" />
              <h3 className="text-sm font-semibold text-foreground">GST Filing Reminders</h3>
            </div>
            <div className="space-y-2">
              {complianceItems.map((item, i) => (
                <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-muted/30 border border-border/50">
                  <div>
                    <p className="text-xs font-medium text-foreground">{item.item}</p>
                    <p className="text-[10px] text-muted-foreground">Due: {item.due}</p>
                  </div>
                  <Badge variant="outline" className={`text-[10px] ${
                    item.status === "filed"
                      ? "bg-green-500/10 text-green-600 border-green-500/20"
                      : "bg-yellow-500/10 text-yellow-600 border-yellow-500/20"
                  }`}>
                    {item.status === "filed" ? "✓ Filed" : "Upcoming"}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recent Service Requests */}
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                <Briefcase className="h-4 w-4 text-accent" /> Recent Service Requests
              </h3>
              <Button size="sm" variant="ghost" className="text-xs gap-1" onClick={() => navigate("/dashboard/service-requests")}>
                View All <ArrowRight className="h-3 w-3" />
              </Button>
            </div>
            {serviceRequests.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
                <p className="text-xs text-muted-foreground">No service requests yet.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {serviceRequests.slice(0, 5).map((req) => {
                  const stsMap: Record<string, string> = {
                    open: "bg-blue-500/10 text-blue-600 border-blue-500/20",
                    in_progress: "bg-accent/10 text-accent border-accent/20",
                    completed: "bg-green-500/10 text-green-600 border-green-500/20",
                    pending: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20",
                  };
                  return (
                    <div key={req.id} className="flex items-center justify-between p-2.5 rounded-lg bg-muted/30 border border-border/50">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-foreground">{req.service_type}</p>
                        <p className="text-[10px] text-muted-foreground truncate">{req.company_name} • {new Date(req.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</p>
                      </div>
                      <Badge variant="outline" className={`text-[10px] ${stsMap[req.status] || "bg-muted text-muted-foreground"}`}>
                        {req.status.replace("_", " ")}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Pending Entries Table */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-1.5">
              <Clock className="h-4 w-4 text-yellow-600" /> Entries Pending Review ({accountingData.pending.length})
            </h3>
            <Button size="sm" variant="ghost" className="text-xs gap-1" onClick={() => navigate("/dashboard/journals")}>
              Review All <ArrowRight className="h-3 w-3" />
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  {["Voucher", "Type", "Narration", "Amount", "Date"].map((h) => (
                    <th key={h} className="text-left py-2 px-3 font-medium text-muted-foreground text-[10px] uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {accountingData.pending.slice(0, 8).map((v) => (
                  <tr key={v.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                    <td className="py-2 px-3 font-medium text-[#2563EB] dark:text-blue-400 hover:text-blue-700 transition-colors text-xs">{v.voucherNumber}</td>
                    <td className="py-2 px-3 text-foreground text-xs">{v.type}</td>
                    <td className="py-2 px-3 text-muted-foreground text-xs truncate max-w-[200px]">{v.narration}</td>
                    <td className="py-2 px-3 font-semibold text-foreground text-xs">₹{v.totalDebit.toLocaleString("en-IN")}</td>
                    <td className="py-2 px-3 text-muted-foreground text-xs">{new Date(v.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</td>
                  </tr>
                ))}
                {accountingData.pending.length === 0 && (
                  <tr><td colSpan={5} className="py-8 text-center text-xs text-muted-foreground">No entries pending review ✓</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Profile Rating */}
      {caProfile && (
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center overflow-hidden">
                  {caProfile.avatar_url ? (
                    <img src={caProfile.avatar_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-primary font-bold text-lg">{caProfile.name?.charAt(0)}</span>
                  )}
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">{caProfile.name}</p>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" /> {Number(caProfile.avg_rating).toFixed(1)} ({caProfile.total_reviews} reviews)
                    </span>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Users className="h-3 w-3" /> {caProfile.total_clients} clients
                    </span>
                  </div>
                </div>
              </div>
              <Button size="sm" variant="outline" className="text-xs gap-1" onClick={() => navigate("/dashboard/ca-profile")}>
                Edit Profile <ArrowRight className="h-3 w-3" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CADashboard;
